/**
 * useCalendlyTrigger Hook
 *
 * Intercepts clicks on special Calendly URLs and triggers the Calendly popup.
 * This allows Contentful-managed buttons to open Calendly without custom components.
 *
 * Special URL patterns:
 * - #calendly-in-person - Opens in-person consultation booking
 * - #calendly-video - Opens video consultation booking
 *
 * @see CL-007: Showroom Details page integration
 * @see CL-011: Error handling and fallback for widget load failures
 */

import React, {useEffect, useCallback, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {Stack, Text} from '@salesforce/retail-react-app/app/components/shared/ui';

import {
  CALENDLY_IN_PERSON_HASH,
  CALENDLY_VIDEO_HASH,
  buildTrustedCalendlyBookingUrl,
  getCalendlyTriggerHash,
  isTrustedCalendlyBookingUrl,
} from '@amblique/king-living/app/constants/calendly-booking-links';
import {useCalendlyBookingReachability} from '@amblique/king-living/app/hooks/use-calendly-booking-reachability';
import {useToast} from '@amblique/king-living/app/hooks/use-toast';

const CALENDLY_WIDGET_SCRIPT = 'https://assets.calendly.com/assets/external/widget.js';
const CALENDLY_WIDGET_CSS = 'https://assets.calendly.com/assets/external/widget.css';
const SCRIPT_LOAD_TIMEOUT_MS = 20000;
const CALENDLY_POLL_INTERVAL_MS = 50;
const CALENDLY_LOADING_ATTR = 'data-calendly-loading';

/** If the shell opens but the iframe never loads (e.g. *.calendly.com blocked while window.Calendly still exists), no postMessage arrives — close and notify. */
const POPUP_EMBED_HEALTH_TIMEOUT_MS = 12000;

/**
 * Ensures Calendly widget.js is loaded, or rejects on error/timeout.
 *
 * @returns {Promise<void>}
 */
const loadCalendlyWidgetScript = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('No window'));
  }

  if (window.Calendly) {
    return Promise.resolve();
  }

  const ensureCss = () => {
    if (document.querySelector(`link[href="${CALENDLY_WIDGET_CSS}"]`)) {
      return;
    }

    const link = document.createElement('link');

    link.href = CALENDLY_WIDGET_CSS;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  };

  let scriptEl = document.querySelector(`script[src="${CALENDLY_WIDGET_SCRIPT}"]`);

  // Dead script from a prior failed load can stay in the DOM; `error` will not fire again. Do not remove while loading (`data-calendly-loading`).
  if (scriptEl && !window.Calendly && scriptEl.getAttribute(CALENDLY_LOADING_ATTR) !== '1') {
    scriptEl.remove();
    scriptEl = null;
  }

  scriptEl = document.querySelector(`script[src="${CALENDLY_WIDGET_SCRIPT}"]`);

  if (!scriptEl) {
    ensureCss();

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');

      script.src = CALENDLY_WIDGET_SCRIPT;
      script.async = true;
      script.setAttribute(CALENDLY_LOADING_ATTR, '1');

      const cleanup = () => {
        clearTimeout(timeoutId);
      };

      const fail = (reason) => {
        cleanup();
        script.removeAttribute(CALENDLY_LOADING_ATTR);
        script.remove();
        reject(new Error(reason));
      };

      const timeoutId = setTimeout(() => {
        fail('timeout');
      }, SCRIPT_LOAD_TIMEOUT_MS);

      script.onload = () => {
        cleanup();
        script.removeAttribute(CALENDLY_LOADING_ATTR);

        if (window.Calendly) {
          resolve();
        } else {
          script.remove();
          reject(new Error('Calendly global missing after load'));
        }
      };

      script.onerror = () => {
        fail('script error');
      };

      document.head.appendChild(script);
    });
  }

  // Script tag already present: wait for Calendly or timeout (in-flight or slow load)
  return new Promise((resolve, reject) => {
    if (window.Calendly) {
      resolve();

      return;
    }

    const deadline = Date.now() + SCRIPT_LOAD_TIMEOUT_MS;
    let done = false;

    const finish = (fn) => {
      if (done) return;
      done = true;
      fn();
    };

    const poll = () => {
      if (window.Calendly) {
        finish(() => resolve());

        return;
      }

      if (Date.now() > deadline) {
        finish(() => {
          scriptEl.removeAttribute(CALENDLY_LOADING_ATTR);
          scriptEl.remove();
          reject(new Error('timeout'));
        });

        return;
      }

      setTimeout(poll, CALENDLY_POLL_INTERVAL_MS);
    };

    scriptEl.addEventListener(
      'error',
      () => {
        finish(() => {
          scriptEl.removeAttribute(CALENDLY_LOADING_ATTR);
          scriptEl.remove();
          reject(new Error('script error'));
        });
      },
      {once: true}
    );

    poll();
  });
};

const initPopupSafely = (url) => {
  if (!window.Calendly?.initPopupWidget) {
    throw new Error('Calendly.initPopupWidget is not available');
  }

  window.Calendly.initPopupWidget({url});
};

/**
 * @param {string} origin - postMessage event.origin
 * @returns {boolean}
 */
const isCalendlyMessageOrigin = (origin) => {
  if (typeof origin !== 'string') {
    return false;
  }

  try {
    const {hostname} = new URL(origin);
    const host = hostname.toLowerCase();

    return host === 'calendly.com' || host.endsWith('.calendly.com');
  } catch {
    return false;
  }
};

/**
 * Opens the popup and recovers when the embed iframe never becomes healthy
 * (common when Calendly is blocked after the widget script already loaded once).
 *
 * @param {string} url - Booking URL passed to initPopupWidget
 * @param {Function} onEmbedUnhealthy - Called after closing a stuck popup
 */
const openCalendlyPopupWithEmbedGuard = (url, onEmbedUnhealthy) => {
  let finished = false;

  const cleanup = () => {
    if (finished) {
      return;
    }

    finished = true;
    clearTimeout(timer);
    window.removeEventListener('message', onMessage);
  };

  const onMessage = (event) => {
    if (isCalendlyMessageOrigin(event.origin)) {
      cleanup();
    }
  };

  const timer = setTimeout(() => {
    cleanup();

    try {
      window.Calendly?.closePopupWidget?.();
    } catch {
      // Ignore if Calendly API is unavailable
    }

    onEmbedUnhealthy();
  }, POPUP_EMBED_HEALTH_TIMEOUT_MS);

  window.addEventListener('message', onMessage);

  try {
    initPopupSafely(url);
  } catch (error) {
    cleanup();
    throw error;
  }
};

/**
 * Hook to intercept Calendly trigger URLs and open popup.
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether Calendly feature is enabled
 * @param {string} options.calendlyShowroomId - The showroom's Calendly ID
 * @param {string} options.inPersonUrlTemplate - URL template for in-person consultations
 * @param {string} options.videoUrlTemplate - URL template for video consultations
 * @param {string} [options.fallbackPhone] - Showroom phone for fallback messaging (CL-011)
 * @param {string} [options.fallbackEmail] - Showroom email for fallback messaging (CL-011)
 */
export const useCalendlyTrigger = ({
  enabled,
  calendlyShowroomId,
  inPersonUrlTemplate,
  videoUrlTemplate,
  fallbackPhone,
  fallbackEmail,
}) => {
  const {formatMessage} = useIntl();
  const showToast = useToast();

  const showWidgetFailureToast = useCallback(() => {
    const lead = formatMessage({
      id: 'calendly.error.widget_load_failed.lead',
      defaultMessage:
        "We're unable to load the booking calendar at the moment. Please try again later.",
    });

    const hasStoreContact = Boolean(fallbackPhone || fallbackEmail);

    const title = (
      <Stack spacing={2} fontSize="sm" textAlign="left" alignItems="stretch" width="100%">
        <Text fontWeight="medium">{lead}</Text>
        {hasStoreContact ? (
          <>
            <Text>
              {formatMessage({
                id: 'calendly.error.widget_load_failed.contact_intro',
                defaultMessage: 'If the issue persists, contact us at:',
              })}
            </Text>
            {fallbackPhone ? (
              <Text>
                <Text as="span" fontWeight="bold">
                  {formatMessage({
                    id: 'calendly.fallback.phone_label',
                    defaultMessage: 'Phone:',
                  })}
                </Text>{' '}
                {fallbackPhone}
              </Text>
            ) : null}
            {fallbackEmail ? (
              <Text>
                <Text as="span" fontWeight="bold">
                  {formatMessage({
                    id: 'calendly.fallback.email_label',
                    defaultMessage: 'Email:',
                  })}
                </Text>{' '}
                {fallbackEmail}
              </Text>
            ) : null}
          </>
        ) : (
          <Text>
            {formatMessage({
              id: 'calendly.fallback.use_contact_section',
              defaultMessage: 'Please use the contact details on this page.',
            })}
          </Text>
        )}
      </Stack>
    );

    showToast({
      id: `calendly-widget-error-${Date.now()}`,
      status: 'error',
      title,
      duration: 10000,
    });
  }, [showToast, formatMessage, fallbackPhone, fallbackEmail]);

  // Generate trusted Calendly URLs (host/protocol), then hide CTAs when the booking page returns 404.
  const trustedInPersonUrl = buildTrustedCalendlyBookingUrl(inPersonUrlTemplate, calendlyShowroomId);
  const trustedVideoUrl = buildTrustedCalendlyBookingUrl(videoUrlTemplate, calendlyShowroomId);

  const shouldProbeReachability = Boolean(
    enabled && calendlyShowroomId && (trustedInPersonUrl || trustedVideoUrl)
  );

  const reachability = useCalendlyBookingReachability({
    enabled: shouldProbeReachability,
    inPersonUrl: trustedInPersonUrl,
    videoUrl: trustedVideoUrl,
  });

  const {inPersonUrl, videoUrl} = useMemo(() => {
    const gate = (trusted, slotReachable) => {
      if (!trusted) {
        return null;
      }

      if (!shouldProbeReachability) {
        return trusted;
      }

      if (reachability.pending) {
        return null;
      }

      if (reachability.fetchError) {
        return trusted;
      }

      return slotReachable ? trusted : null;
    };

    return {
      inPersonUrl: gate(trustedInPersonUrl, reachability.inPersonReachable),
      videoUrl: gate(trustedVideoUrl, reachability.videoReachable),
    };
  }, [
    trustedInPersonUrl,
    trustedVideoUrl,
    shouldProbeReachability,
    reachability.pending,
    reachability.fetchError,
    reachability.inPersonReachable,
    reachability.videoReachable,
  ]);

  const openCalendlyPopup = useCallback(
    async (url) => {
      if (!url) {
        showToast({
          id: `calendly-config-error-${Date.now()}`,
          status: 'error',
          title: formatMessage({
            id: 'calendly.error.invalid_booking_link',
            defaultMessage:
              'Booking is not available due to a configuration issue. Please contact the showroom.',
          }),
        });

        return;
      }

      if (!isTrustedCalendlyBookingUrl(url)) {
        showToast({
          id: `calendly-url-error-${Date.now()}`,
          status: 'error',
          title: formatMessage({
            id: 'calendly.error.untrusted_url',
            defaultMessage: 'This booking link is not valid. Please contact the showroom.',
          }),
        });

        return;
      }

      try {
        if (typeof window !== 'undefined' && window.Calendly) {
          openCalendlyPopupWithEmbedGuard(url, showWidgetFailureToast);

          return;
        }

        await loadCalendlyWidgetScript();
        openCalendlyPopupWithEmbedGuard(url, showWidgetFailureToast);
      } catch (error) {
        console.warn('[Calendly] Widget load or open failed:', error);
        showWidgetFailureToast();
      }
    },
    [showToast, formatMessage, showWidgetFailureToast]
  );

  // Click handler for intercepting Calendly trigger URLs
  const handleClick = useCallback(
    (event) => {
      // Only process if Calendly is enabled and we have a showroom ID
      if (!enabled || !calendlyShowroomId) {
        return;
      }

      // Find the closest anchor element or button
      const target = event.target.closest('a, button');

      if (!target) return;

      const href = target.getAttribute('href');

      if (!href) return;

      const triggerHash = getCalendlyTriggerHash(href);

      if (triggerHash === CALENDLY_IN_PERSON_HASH) {
        event.preventDefault();
        event.stopPropagation();
        if (inPersonUrl) {
          void openCalendlyPopup(inPersonUrl);
        }

        return false;
      } else if (triggerHash === CALENDLY_VIDEO_HASH) {
        event.preventDefault();
        event.stopPropagation();
        if (videoUrl) {
          void openCalendlyPopup(videoUrl);
        }

        return false;
      }
    },
    [enabled, calendlyShowroomId, inPersonUrl, videoUrl, openCalendlyPopup]
  );

  // Handle hash changes (for React Router navigation)
  const handleHashChange = useCallback(() => {
    const triggerHash = getCalendlyTriggerHash(window.location.hash);

    if (triggerHash === CALENDLY_IN_PERSON_HASH && inPersonUrl) {
      // Clear the hash to prevent re-triggering
      window.history.replaceState(null, '', window.location.pathname);
      void openCalendlyPopup(inPersonUrl);
    } else if (triggerHash === CALENDLY_VIDEO_HASH && videoUrl) {
      // Clear the hash to prevent re-triggering
      window.history.replaceState(null, '', window.location.pathname);
      void openCalendlyPopup(videoUrl);
    }
  }, [inPersonUrl, videoUrl, openCalendlyPopup]);

  // Check hash on mount and when it changes
  useEffect(() => {
    if (!enabled || !calendlyShowroomId) {
      return;
    }

    // Check current hash on mount
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [enabled, calendlyShowroomId, handleHashChange]);

  // Add click listener to document
  useEffect(() => {
    if (!enabled || !calendlyShowroomId) {
      // Log warning only if partially configured (enabled but missing showroomId)
      if (enabled && !calendlyShowroomId) {
        console.warn(
          '[Calendly] Feature enabled but no showroom Calendly segment is available yet (saved showroom, nearest store, or showroom page).'
        );
      }

      return;
    }

    // Use capture phase to intercept before other handlers
    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [enabled, calendlyShowroomId, handleClick]);

  // Return URLs for manual triggering if needed
  return {
    inPersonUrl,
    videoUrl,
    openInPersonPopup: () => void openCalendlyPopup(inPersonUrl),
    openVideoPopup: () => void openCalendlyPopup(videoUrl),
  };
};

export {isTrustedCalendlyBookingUrl} from '@amblique/king-living/app/constants/calendly-booking-links';

export default useCalendlyTrigger;
